// In-memory translation cache — persists across re-renders within same session
const cache = new Map();

/**
 * Translate text using MyMemory API (free, no API key needed).
 * Auto-detects source language and translates to targetLang.
 * Returns { translatedText, detectedLang } or null if already in target language / failed.
 */
export async function translateMessage(text, targetLang = 'en') {
  if (!text || text.trim().length === 0) return null;

  const key = `${text}::${targetLang}`;
  if (cache.has(key)) return cache.get(key);

  try {
    const res = await fetch(
      `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text.slice(0, 500))}&langpair=autodetect|${targetLang}`
    );

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const data = await res.json();
    const translated = data?.responseData?.translatedText;
    const detectedLang = data?.responseData?.detectedLanguage || null;

    // If the translation is the same as the original (case-insensitive), it's already in the target language
    if (!translated || translated.toLowerCase().trim() === text.toLowerCase().trim()) {
      cache.set(key, null);
      return null;
    }

    // MyMemory sometimes returns PLEASE SELECT TWO LANGUAGES or similar error strings
    if (translated.startsWith('PLEASE SELECT') || translated.startsWith('MYMEMORY WARNING')) {
      return null;
    }

    const result = { translatedText: translated, detectedLang };
    cache.set(key, result);
    return result;
  } catch (err) {
    console.error('Translation failed:', err);
    return null;
  }
}

/**
 * Detect the language of a text string using MyMemory.
 * Returns ISO language code (e.g. "fr", "es", "ar") or null.
 */
export async function detectLanguage(text) {
  if (!text || text.trim().length === 0) return null;

  try {
    const res = await fetch(
      `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text.slice(0, 200))}&langpair=autodetect|en`
    );
    if (!res.ok) return null;

    const data = await res.json();
    return data?.responseData?.detectedLanguage || null;
  } catch {
    return null;
  }
}
